import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Target, UserSearch, Handshake, Network, BadgeCheck, Timer, Wallet, ArrowUpRight, BadgeDollarSign, MapPinned, Landmark, ArrowRight } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { Reveal, Overline } from "../Reveal";
import { CountUp } from "../CountUp";

const HL_ICONS = [BadgeDollarSign, MapPinned, Landmark];

export const HighlightBox = ({ items, note, testid = "highlight-box" }) => (
  <Reveal>
    <div data-testid={testid} className="relative rounded-3xl bg-[#1B2A4E] text-white p-6 sm:p-8 overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-20" />
      <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-[#E8842C]/25 blur-3xl" />
      <div className="relative grid md:grid-cols-3 gap-4">
        {items.map((h, i) => {
          const Icon = HL_ICONS[i];
          return (
            <div key={i} className="flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 p-4 group hover:bg-[#E8842C] transition-colors duration-300">
              <span className="w-12 h-12 shrink-0 rounded-xl bg-[#E8842C] group-hover:bg-white flex items-center justify-center transition-colors">
                <Icon className="w-6 h-6 text-white group-hover:text-[#E8842C] transition-colors" />
              </span>
              <p className="font-head font-black text-sm sm:text-base leading-snug">{h}</p>
            </div>
          );
        })}
      </div>
      {note && <p className="relative text-xs text-white/60 mt-4 font-bold tracking-wide">{note}</p>}
    </div>
  </Reveal>
);

const StepList = ({ steps, testid }) => (
  <div className="relative" data-testid={testid}>
    <div className="absolute top-0 bottom-0 start-6 w-px bg-gradient-to-b from-[#E8842C] via-[#1B2A4E]/15 to-transparent" />
    <div className="space-y-5">
      {steps.map((s, i) => (
        <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }} className="flex gap-5 items-start group">
          <div className="relative z-10 w-12 h-12 shrink-0 rounded-full bg-white border-2 border-[#1B2A4E]/15 flex items-center justify-center font-head font-black text-[#E8842C] text-sm group-hover:bg-[#E8842C] group-hover:border-[#E8842C] group-hover:text-white group-hover:scale-110 transition-all duration-300" dir="ltr">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="card-light p-5 flex-1">
            <h4 className="font-head font-bold text-[#1B2A4E]">{s.t}</h4>
            <p className="text-sm text-[#1B2A4E]/65 mt-1.5 leading-relaxed">{s.d}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export const DestinationTabs = ({ ns, testid }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("sa");
  const tabs = [
    { key: "sa", flag: "🇸🇦", label: t(`${ns}.tab_sa`) },
    { key: "tr", flag: "🇹🇷", label: t(`${ns}.tab_tr`) },
  ];
  return (
    <section className="py-24 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid={testid}>
      <div className="max-w-5xl mx-auto">
        <Reveal className="text-center max-w-3xl mx-auto">
          <div className="flex justify-center"><Overline>{t(`${ns}.proc_overline`)}</Overline></div>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t(`${ns}.proc_title`)}</h2>
        </Reveal>
        <div className="flex justify-center mt-10">
          <div className="inline-flex rounded-full border border-[#1B2A4E]/12 bg-[#FAF7F2] p-1.5 gap-1">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                data-testid={`${testid}-tab-${tb.key}`}
                onClick={() => setTab(tb.key)}
                className={`px-5 sm:px-7 py-3 rounded-full text-sm font-black transition-all duration-300 flex items-center gap-2 ${tab === tb.key ? "bg-[#1B2A4E] text-white shadow-lg" : "text-[#1B2A4E]/60 hover:text-[#1B2A4E]"}`}
              >
                <span className="text-lg leading-none">{tb.flag}</span> {tb.label}
              </button>
            ))}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="mt-12">
            <div className="text-center mb-10">
              <h3 className="font-head font-black text-2xl sm:text-3xl text-[#1B2A4E]">{tabs.find((x) => x.key === tab).flag} {t(`${ns}.${tab}_title`)}</h3>
              <p className="text-[#1B2A4E]/60 mt-3 max-w-2xl mx-auto">{t(`${ns}.${tab}_sub`)}</p>
            </div>
            <StepList steps={t(`${ns}.${tab}_steps`)} testid={`${testid}-steps-${tab}`} />
            {ns === "empov" && (
              <div className="mt-12 rounded-3xl border border-[#E8842C]/30 bg-[#FAF7F2] p-6 sm:p-8" data-testid="commitment-box">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E8842C] mb-4">{t("empov.commit_title")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {t("empov.commit_flow").map((f, i, arr) => (
                    <span key={i} className="flex items-center gap-2">
                      <span className="px-3.5 py-2 rounded-full bg-white border border-[#1B2A4E]/10 text-sm font-bold text-[#1B2A4E]">{f}</span>
                      {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-[#E8842C] rtl:rotate-180" />}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-[#1B2A4E]/65 mt-4 leading-relaxed">{t("empov.commit_note")}</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const FEAT_ICONS = [Target, UserSearch, Handshake];

export function EmployerOverview() {
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  return (
    <section className="py-24 px-6 sm:px-12" data-testid="employer-overview">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>{t("empov.overline")}</Overline>
            <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E] leading-tight">{t("empov.title")}</h2>
            <p className="text-[#1B2A4E]/65 mt-6 leading-relaxed text-lg">{t("empov.sub")}</p>
            <button data-testid="employer-hire-now" onClick={() => document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" })} className="btn-primary mt-8 group">
              {t("empov.cta")} <ArrowUpRight className={`w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform ${isRtl ? "-scale-x-100" : ""}`} />
            </button>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            {t("empov.stats").map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`employer-stat-${i}`} className={`card-light p-6 ${i % 2 === 1 ? "translate-y-6" : ""}`}>
                  <div className="font-head font-black text-4xl sm:text-5xl text-[#E8842C]" dir="ltr"><CountUp to={s.to} suffix={s.suffix} /></div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#1B2A4E]/55 mt-2">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-24">
          <Reveal className="text-center"><div className="flex justify-center"><Overline>{t("empov.feat_overline")}</Overline></div></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {t("empov.feats").map((f, i) => {
              const Icon = FEAT_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div data-testid={`employer-feat-${i}`} className="card-light p-8 h-full group">
                    <span className="font-head font-black text-5xl text-[#1B2A4E]/5 group-hover:text-[#E8842C]/15 transition-colors" dir="ltr">0{i + 1}.</span>
                    <div className="w-14 h-14 rounded-2xl bg-[#1B2A4E] flex items-center justify-center -mt-4 mb-5 group-hover:bg-[#E8842C] group-hover:rotate-6 transition-all"><Icon className="w-7 h-7 text-white" /></div>
                    <h3 className="font-head font-bold text-xl text-[#1B2A4E] mb-3">{f.t}</h3>
                    <p className="text-sm text-[#1B2A4E]/60 leading-relaxed">{f.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
        <div className="mt-16">
          <HighlightBox items={t("empov.highlight")} note={t("empov.highlight_note")} testid="employer-highlight" />
        </div>
      </div>
    </section>
  );
}

const BEN_ICONS = [Network, BadgeCheck, Timer, Wallet];

export function JobSeekerOverview() {
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  return (
    <section className="py-24 px-6 sm:px-12" data-testid="jobseeker-overview">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <Reveal>
            <Overline>{t("jsov.overline")}</Overline>
            <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E] leading-tight">{t("jsov.title")}</h2>
            <p className="text-[#1B2A4E]/65 mt-6 leading-relaxed text-lg">{t("jsov.sub")}</p>
            <button data-testid="jobseeker-apply-now" onClick={() => navigate("/apply")} className="btn-primary mt-8 group">
              {t("jsov.cta")} <ArrowUpRight className={`w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform ${isRtl ? "-scale-x-100" : ""}`} />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl shadow-[#1B2A4E]/20">
              <img src="/assets/hero-1.jpg" alt="Job seekers" loading="lazy" className="w-full h-80 object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="mt-24">
          <Reveal className="text-center"><div className="flex justify-center"><Overline>{t("jsov.benefits_overline")}</Overline></div></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {t("jsov.benefits").map((b, i) => {
              const Icon = BEN_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`jobseeker-benefit-${i}`} className="card-light p-7 h-full group">
                    <div className="w-14 h-14 rounded-2xl bg-[#FAF7F2] border border-[#1B2A4E]/10 flex items-center justify-center mb-5 group-hover:bg-[#E8842C] group-hover:border-[#E8842C] group-hover:-rotate-6 group-hover:scale-110 transition-all"><Icon className="w-7 h-7 text-[#E8842C] group-hover:text-white transition-colors" /></div>
                    <h3 className="font-head font-bold text-lg text-[#1B2A4E] mb-2">{b.t}</h3>
                    <p className="text-sm text-[#1B2A4E]/60 leading-relaxed">{b.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
